import { Injectable } from "@angular/core";
import { MappedExportRequest } from "app/models/export-requests/export-request.models";
import {
  ConfiguredMessageTarget,
  ExportRequest,
  ExportRequestCreate,
  ExportRequestGenericOntology,
  ExportRequestList,
  ExportRequestTagOntology,
  ExportRequestTimeline, ExportRequestTransactionOntology,
  ExportRequestUpdate,
} from "swagger-gen/web-secured/models";

import * as _ from 'lodash';
import { TranslateService } from "@ngx-translate/core";
import { FormGroup } from "@angular/forms";
import { ExportResquestForm } from "@gnv-shared/types";
import { OntologyType } from "@gnv-shared/models";
import { parseExportRequestConfiguredOntologies } from "@gnv-shared/utils";
import { ExportRequestStateOntology } from '../../../../swagger-gen/gnvert-secured/models/export-request-state-ontology';

@Injectable({
  providedIn: "root",
})
export class ExportRequestMapper {
  constructor(public translate: TranslateService) {
  }

  public mapEntities(entities: ExportRequestList): MappedExportRequest[] {
    return entities.map(entity => this.mapToTableEntity(entity));
  }

  public mapToTableEntity(entity: ExportRequest): MappedExportRequest {
    return {
      id: entity.uid ?? "", // Map the UID to "id"
      name: entity.name, // Keep the name
      creator: entity.createdBy ?? "", // Map the createdBy to "creator"
      createdOn: entity.createdAt ?? "", // Map createdAt to "createdOn"
      lastModifiedBy: entity.modifiedBy ?? "", // Map modifiedBy to "lastModifiedBy"
      lastModifiedOn: entity.modifiedAt ?? "", // Map modifiedAt to "lastModifiedOn"
      frequency: this.getFrequencyLabel(entity),
      frequencyKey: entity.frequency,
      state: this.translate.instant('export-request.table.row.state.' + (entity.state ?? 'unknown')),
      stateKey: entity.state,
      description: "",
      configuredOntologies: parseExportRequestConfiguredOntologies(entity.configuredOntologies || []),
      modelUid: '',
      lastExecutionDate: _.last(entity.timeline)?.dateTime || '',
      active: entity.active as boolean,
      timelineErrorMessage: this.getTimelineErrorMessage(entity),
    };
  }

  formToUpdateRequestEntity(
    requestFormGroup: FormGroup<ExportResquestForm>,
  ): ExportRequestUpdate {
    return {
      name: requestFormGroup.controls.name.value,
      description: "",
      active: true,
      frequency: requestFormGroup.controls.frequency.value || undefined,
      destination: requestFormGroup.controls.destination.value as ConfiguredMessageTarget,
      withoutHeaders: requestFormGroup.controls.withoutHeaders.value,
    }
  }

   formToCreateRequestEntity(
    requestFormGroup: FormGroup<ExportResquestForm>,
    modelUid: string
  ): ExportRequestCreate {
    return {
      name: requestFormGroup.controls.name.value,
      description: "",
      active: true,
      frequency: requestFormGroup.controls.frequency.value || undefined,
      destination: requestFormGroup.controls.destination.value as ConfiguredMessageTarget,
      modelUid: modelUid,
      withoutHeaders: requestFormGroup.controls.withoutHeaders.value,
    }
  }


  /**
 * Transforme un `FormGroup` contenant des informations des formulaires d'ontologies en une liste d'objets de type `ExportRequestGenericOntology`.
 *
 * @param requestFormGroup - Le `FormGroup` contenant les données du formulaire à transformer..
 * @returns Une liste d'objets `{ontology: ExportRequestGenericOntology, type: OntologyType}` qui représente les informations de chaque formulaire d'ontologie avec le type.
 */
  formToOntologyEntities(
    requestFormGroup: FormGroup<ExportResquestForm>
  ): {ontology: ExportRequestGenericOntology | ExportRequestTagOntology | ExportRequestTransactionOntology | ExportRequestStateOntology, type: OntologyType, fullPath: string}[] {
    const ontologyForms = requestFormGroup?.controls?.ontologyForms?.value || [];

    return ontologyForms.map((item) => {
      // TODO: Change any to a type
      const ontology: any = {
        sheetName: item?.name || "",
        stationUids: item?.stations || [],
        energies: item.energies,
        sampling: item.sampling,
        subscriptionUids: item?.subscriptions,
        provider: item?.provider,
      };

      if (item.type === OntologyType.ANALOGIC) {
        ontology.calculationFunction = item.calculationFunctionType;
        ontology.dataTimeStepType = item.dataTimeStepType;
        ontology.dataTimeStepRange = item.dataTimeStepRange;
      }

      if (item.tags && item.tags.length > 0) {
        ontology.tagUids = item?.tags
      } else {
        ontology.tagUids = undefined;
      }

      if (item?.interval) {
        ontology.interval = item?.interval;
      } else if (item && item.dateRanges) {
        ontology.startDateTime = item?.dateRanges?.startDateTime?.toISOString();
        ontology.endDateTime = item?.dateRanges?.endDateTime?.toISOString();
      }

      return {
        ontology,
        type: item.type || OntologyType.UNKNOWN,
        fullPath:item.path || '',
      };
    });
  }

  private getTimelineErrorMessage(entity: ExportRequest): string | undefined {
    if (entity.state === "ERROR") {
      const lastTimeline = Object.assign([], entity.timeline).pop() as unknown as ExportRequestTimeline;
      return lastTimeline.comment
    }

    return '';
  }

  private getFrequencyLabel(entity: ExportRequest) {
    const frequency = entity?.frequency?.toLowerCase()?.trim();
    return frequency ? this.translate.instant(`export-creation.form.recurrence.${frequency}`) : '';
  }
}
